import api from './api';
import type { InstagramConnectionData } from '@app-types/models/instagram';
import type { Showcase } from '@app-types/models/showcase';
import type { User } from '@app-types/models/user';

interface InstagramOAuthResponse {
  success: boolean;
  authUrl: string;
  token?: string;
}

interface ExistingShowcasesResponse {
  success: boolean;
  count: number;
  showcases: Array<{
    id: string;
    [key: string]: unknown;
  }>;
}

interface DisconnectResponse {
  success: boolean;
  message: string;
}

interface ShowcaseBundleResponse {
  success: boolean;
  message?: string;
  tempAccessToken?: string;
  showcaseIds?: string[];
  data: {
    business?: { _id: string; [key: string]: unknown };
    staff?: { _id: string; [key: string]: unknown };
    showcases?: Showcase[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface DeleteShowcaseResponse {
  success: boolean;
  message?: string;
}

interface ClaimAccountData {
  email: string;
  password?: string;
  instagramOnboardingToken?: string;
  [key: string]: unknown;
}

interface ClaimAccountResponse {
  success: boolean;
  token?: string;
  user?: User;
  businessId?: string;
  staffId?: string;
  message?: string;
}

interface AiStatusResponse {
  success: boolean;
  analysisComplete?: boolean;
  analysisInProgress?: boolean;
  analysisFailed?: boolean;
  status?: string;
  hasAnalysisData?: boolean;
  lastError?: string;
  data?: unknown;
}

interface RetryAnalysisResponse {
  success: boolean;
  attempts?: number;
}

interface AutoLoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  redirectTo?: string;
}

interface ShowcaseUpdateData {
  content: string;
  _id: string;
}

interface UpdateShowcaseWithTokenResponse {
  success: boolean;
  data?: Showcase;
}

// Instagram showcase flow specific API calls

/**
 * Initialize Instagram OAuth for the showcase flow
 * This initiates OAuth flow and returns JWT token upon success
 */
export const initInstagramOAuth = async (returnUrl: string): Promise<InstagramOAuthResponse> => {
  try {
    const response = await api.post<InstagramOAuthResponse>('/public/instagram/init-oauth', {
      returnUrl
    });
    return response.data;
  } catch (error: unknown) {
    console.error('Error initiating Instagram OAuth:', error);
    throw error;
  }
};

/**
 * Verify Instagram connection status using JWT token
 */
export const verifyInstagramConnection = async (token: string): Promise<InstagramConnectionData> => {
  try {
    const response = await api.get<InstagramConnectionData>('/public/instagram/connection-status', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error: unknown) {
    console.error('Error verifying Instagram connection:', error);
    throw error;
  }
};

/**
 * Get existing showcases for a staff member
 */
export const getExistingShowcases = async (staffId: string): Promise<ExistingShowcasesResponse> => {
  try {
    console.log('Fetching existing showcases for staff:', staffId);

    const response = await api.get<ExistingShowcasesResponse>(`/public/instagram/existing-showcases?staffId=${staffId}`);

    console.log('Existing showcases result:', {
      success: response.data.success,
      count: response.data.count,
      showcaseIds: response.data.showcases?.map((s: { id: string }) => s.id) || []
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching existing showcases:', error);
    throw error;
  }
};

/**
 * Disconnect Instagram (client-side only)
 * Removes the JWT token from localStorage
 */
export const disconnectInstagram = async (): Promise<DisconnectResponse> => {
  try {
    // Remove token from localStorage (no backend call needed - JWT-based)
    localStorage.removeItem('instagram_onboarding_token');

    console.log('Instagram disconnected (client-side)');

    return {
      success: true,
      message: 'Instagram disconnected successfully'
    };
  } catch (error: unknown) {
    console.error('Error disconnecting Instagram:', error);
    throw error;
  }
};

/**
 * Create showcase bundle - the critical endpoint that creates everything
 * This creates business, staff, showcases and triggers AI analysis
 */
export const createShowcaseBundle = async (bundleData: FormData): Promise<ShowcaseBundleResponse> => {
  try {
    // Extract instagramOnboardingToken from formData and add to Authorization header
    const instagramOnboardingToken = bundleData.get('instagramOnboardingToken') as string | null;

    console.log('Creating showcase bundle with data:', {
      hasInstagramOnboardingToken: !!instagramOnboardingToken,
      instagramUserId: bundleData.get('instagramUserId'),
      instagramUsername: bundleData.get('instagramUsername'),
      selectedPostsCount: bundleData.get('selectedInstagramPosts') ?
        JSON.parse(bundleData.get('selectedInstagramPosts') as string).length : 0,
      uploadedFilesCount: bundleData.getAll('uploadedFiles').length
    });

    // Remove instagramOnboardingToken from formData since it goes in header
    bundleData.delete('instagramOnboardingToken');

    const response = await api.post<ShowcaseBundleResponse>('/public/instagram/create-showcase-bundle', bundleData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(instagramOnboardingToken && { 'Authorization': `Bearer ${instagramOnboardingToken}` })
      },
      timeout: 60000
    });

    console.log('Showcase bundle creation response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error creating showcase bundle:', error);
    throw error;
  }
};

/**
 * Delete showcase using Instagram onboarding token
 */
export const deleteShowcaseWithToken = async (showcaseId: string, instagramOnboardingToken: string): Promise<DeleteShowcaseResponse> => {
  try {
    console.log('Deleting showcase with token:', { showcaseId });

    const response = await api.delete<DeleteShowcaseResponse>('/public/instagram/delete-showcase', {
      data: { showcaseId },
      headers: {
        Authorization: `Bearer ${instagramOnboardingToken}`
      }
    });

    console.log('Delete successful:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting showcase with token:', error);
    throw error;
  }
};

/**
 * Claim temporary account by converting to permanent with email/password
 */
export const claimAccount = async (claimData: ClaimAccountData): Promise<ClaimAccountResponse> => {
  try {
    console.log('Claiming account with:', {
      email: claimData.email,
      hasInstagramOnboardingToken: !!claimData.instagramOnboardingToken
    });

    const response = await api.post<ClaimAccountResponse>('/public/account/claim', claimData);

    console.log('Account claim response:', {
      success: response.data.success,
      hasToken: !!response.data.token,
      hasUser: !!response.data.user
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Error claiming account:', error);
    throw error;
  }
};

/**
 * Check AI analysis status for a staff member
 */
export const checkAiAnalysisStatus = async (staffId: string, instagramOnboardingToken: string): Promise<AiStatusResponse> => {
  try {
    // Add cache-busting timestamp to prevent 304 responses
    const timestamp = Date.now();
    const response = await api.get<AiStatusResponse>(`/public/instagram/ai-status/${staffId}?t=${timestamp}`, {
      headers: {
        Authorization: `Bearer ${instagramOnboardingToken}`
      }
    });

    console.log('AI analysis status:', {
      analysisComplete: response.data.analysisComplete,
      analysisInProgress: response.data.analysisInProgress,
      analysisFailed: response.data.analysisFailed,
      status: response.data.status,
      hasAnalysisData: !!response.data.hasAnalysisData
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Error checking AI analysis status:', error);
    throw error;
  }
};

/**
 * Retry AI analysis for a staff member
 */
export const retryAIAnalysis = async (staffId: string, instagramOnboardingToken: string): Promise<RetryAnalysisResponse> => {
  try {
    console.log('Retrying AI analysis for staff:', { staffId });

    const response = await api.post<RetryAnalysisResponse>(`/public/instagram/retry-analysis/${staffId}`, {}, {
      headers: {
        Authorization: `Bearer ${instagramOnboardingToken}`
      }
    });

    console.log('AI analysis retry response:', {
      success: response.data.success,
      attempts: response.data.attempts
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Error retrying AI analysis:', error);
    throw error;
  }
};

/**
 * Auto-login for returning users who already claimed their account
 * Generates a full authentication token without requiring email/password
 */
export const autoLoginClaimedAccount = async (instagramToken: string): Promise<AutoLoginResponse> => {
  try {
    console.log('Auto-login for returning user...');

    const response = await api.post<AutoLoginResponse>('/public/account/auto-login', {}, {
      headers: {
        Authorization: `Bearer ${instagramToken}`
      }
    });

    console.log('Auto-login response:', {
      success: response.data.success,
      hasToken: !!response.data.token,
      hasUser: !!response.data.user
    });

    return response.data;
  } catch (error: unknown) {
    console.error('Error in auto-login:', error);
    throw error;
  }
};

/**
 * Update showcase using Instagram onboarding token
 * Used during onboarding flow when user is not yet logged in
 * Only updates text content (no media upload support)
 */
export const updateShowcaseWithToken = async (showcaseData: ShowcaseUpdateData, instagramOnboardingToken: string): Promise<UpdateShowcaseWithTokenResponse> => {
  try {
    const { content, _id } = showcaseData;

    console.log('updateShowcaseWithToken called with:', {
      showcaseId: _id,
      content
    });

    const response = await api.put<UpdateShowcaseWithTokenResponse>('/public/instagram/update-showcase', {
      _id,
      content
    }, {
      headers: {
        Authorization: `Bearer ${instagramOnboardingToken}`
      }
    });

    console.log('Update successful, response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating showcase with token:', error);
    const axiosErr = error as { response?: { data?: unknown } };
    console.error('Error response:', axiosErr.response?.data);
    throw error;
  }
};

/**
 * Check Instagram session status from the backend
 * Used by legacy InstagramConnectStep for session-based OAuth flow
 */
interface InstagramSessionStatusResponse {
  connected: boolean;
  token?: string;
  userId?: string;
  username?: string;
  posts?: Array<{ id: string; [key: string]: unknown }>;
  connectedAt?: string;
  aiAnalysisComplete?: boolean;
  aiAnalysis?: unknown;
  aiAnalysisInProgress?: boolean;
  message?: string;
  [key: string]: unknown;
}

export const checkInstagramSessionStatus = async (): Promise<InstagramSessionStatusResponse> => {
  try {
    const response = await api.get<InstagramSessionStatusResponse>('/public/instagram/session-status');
    return response.data;
  } catch (error: unknown) {
    console.error('Error checking Instagram session status:', error);
    throw error;
  }
};
